import type { ParseResult } from '../../../shared/api/types'
import type { ItemRow } from '../components/ItemsTable'
import { makeRowUid } from '../components/ItemsTable'
import { isKnownBrand } from './brandMatch'
import { detectHomoglyph, type HomoglyphSeg } from './homoglyph'

export type ParsedLineStatus = {
  line: string
  lineNum: number
  status: 'ok' | 'warn' | 'skipped'
  /** Тексты предупреждений (нет бренда/кол-ва) — показываем под строкой в
   *  превью, чтобы было видно ЧТО не так, а не только цвет. */
  warnings?: string[]
  /** Кириллица-двойник в артикуле: сегменты для рендера красной буквы + канон. */
  homoglyph?: { segments: HomoglyphSeg[]; canonical: string }
}

/** Разбивает исходный текст вставки на непустые строки (trim). */
export function splitNonEmptyLines(rawText: string): string[] {
  return rawText
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
}

/** Канонизация строки для сравнения исходной (как ввёл пользователь) и rawLine
 *  из парсера: trim + схлопывание подряд идущих whitespace в один пробел.
 *  Парсер часто нормализует табы/двойные пробелы → строгое сравнение `===`
 *  даёт false negative, и нормальная позиция уезжает в «skipped» (серый «—»)
 *  в превью, а в нижней таблице теряет порядковый номер. */
const normalizeForMatch = (s: string) => s.trim().replace(/\s+/g, ' ')

/** Исходная строка заказчика без хвостового количества — для колонки «Артикул
 *  заказчика» в КП. Срезаем с конца количество (с опциональной единицей), но
 *  только если перед ним есть разделитель — чтобы не отрезать цифры, слитные с
 *  партномером (напр. «EQ25/LP-3C95»). Если количество не распознано — строку
 *  оставляем как есть. Бренд, если клиент написал его в строке, сохраняется. */
function stripTrailingQty(rawLine: string, qty: number | null): string {
  const s = rawLine.trim()
  if (qty == null || qty <= 0) return s
  const stripped = s.replace(/[\s\t]+\d[\d\s.,]*\s*(?:шт\.?|штук|ед\.?|pcs\.?|pieces?)?\s*$/i, '')
  return stripped.trim() || s
}

type RowLike = ParseResult['rows'][number]

/** Единый расчёт warnings для распознанной row — используется и в превью
 *  (buildParsedLines), и в таблице позиций (mapRowsToItems), чтобы статусы
 *  в обоих местах совпадали. Раньше превью смотрело только на
 *  row.needsManualReview, а таблица доп. валидировала поля — в итоге одна
 *  и та же позиция могла быть ✓ сверху и красной снизу. */
function computeRowWarnings(p: RowLike): string[] {
  const brand = (p.brand ?? '').trim()
  const partNumberRaw = (p.partNumber ?? '').trim()
  const warnings: string[] = [...(p.parseWarnings ?? [])]
  if (!partNumberRaw) warnings.push('Партномер не распознан')
  if (!brand) warnings.push('Бренд не распознан')
  if (p.qty == null || p.qty <= 0) warnings.push('Количество не распознано')
  return warnings
}

/** Собирает аннотированный список строк текста с их статусом (ok/warn/skipped).
 *  Статус берётся из result.skipped + computeRowWarnings (единый расчёт с таблицей). */
export function buildParsedLines(rawText: string, result: ParseResult): ParsedLineStatus[] {
  const nonEmptyLines = splitNonEmptyLines(rawText)
  const skippedNorm = new Set(result.skipped.map(normalizeForMatch))
  return nonEmptyLines.map((line, idx) => {
    const lineNum = idx + 1
    const lineNorm = normalizeForMatch(line)
    const row = result.rows.find((r) => normalizeForMatch(r.rawLine) === lineNorm)
    if (row) {
      const hg = detectHomoglyph(row.partNumber)
      // Текст про кириллицу показываем отдельной rich-аннотацией (с красной
      // буквой), поэтому исключаем дублирующий текст-варнинг из общего списка.
      const warnings = computeRowWarnings(row).filter((w) => !/кириллиц/i.test(w))
      const isWarn = row.needsManualReview || warnings.length > 0 || hg.has
      return {
        line,
        lineNum,
        status: isWarn ? 'warn' : 'ok',
        warnings,
        ...(hg.has ? { homoglyph: { segments: hg.segments, canonical: hg.canonical } } : {}),
      }
    }
    if (skippedNorm.has(lineNorm)) return { line, lineNum, status: 'skipped' }
    return { line, lineNum, status: 'skipped' }
  })
}

/** Результат `mapRowsToItems`: items для таблицы + extras — короткие описания
 *  rows, которые парсер вернул сверх исходного текста (расщеплённые/синтетические
 *  rawLine, не находящие соответствия в rawText). Они НЕ идут в таблицу
 *  (чтобы не «дробить» нумерацию), но их данные показываются в общем
 *  warning-блоке, чтобы менеджер не потерял информацию. */
export type MapRowsResult = {
  items: ItemRow[]
  extras: string[]
}

/** Преобразует rows распознавания в строки таблицы позиций.
 *  Битые строки (без партномера/бренда/кол-ва) помечаются needsManualReview=true
 *  и выключены (checked=false) — менеджер исправит/отметит руками.
 *
 *  Нумерация: каждой row из result.rows присваивается номер исходной строки
 *  в `rawText`. Для дублей (одинаковый rawLine встречается несколько раз)
 *  расходуем строки **по порядку**: первая встреча → первый row, вторая → второй.
 *  Раньше использовался Map<string, number>, который для дублей возвращал
 *  всегда первый индекс → в нижней таблице получалось «1,2,1,4» вместо «1,2,3,4». */
export function mapRowsToItems(
  rawText: string,
  result: ParseResult,
  knownBrands: Set<string>,
): MapRowsResult {
  const nonEmptyLines = splitNonEmptyLines(rawText)
  const usedLineIdx = new Array<boolean>(nonEmptyLines.length).fill(false)
  // Сравниваем по нормализованной форме (см. normalizeForMatch выше): парсер
  // на бэке схлопывает табы/двойные пробелы, и строгое `===` промахивается →
  // lineNumber=null → в таблице у такой позиции пустой порядковый номер.
  const findLineNum = (rawLine: string): number | null => {
    const target = normalizeForMatch(rawLine)
    for (let i = 0; i < nonEmptyLines.length; i++) {
      if (!usedLineIdx[i] && normalizeForMatch(nonEmptyLines[i] ?? '') === target) {
        usedLineIdx[i] = true
        return i + 1
      }
    }
    return null
  }

  const rowItems: ItemRow[] = result.rows.map((p) => {
    const brand = (p.brand ?? '').trim()
    const partNumberRaw = (p.partNumber ?? '').trim()
    const matched = isKnownBrand(knownBrands, brand)
    const warnings = computeRowWarnings(p)
    // Кириллица-двойник в артикуле — всегда «на проверку» (даже если парт не в
    // справочнике): партномер испорчен, его надо поправить до отправки.
    const hasHomoglyph = detectHomoglyph(partNumberRaw).has
    const needsReview = p.needsManualReview || warnings.length > 0 || hasHomoglyph
    const brandInput = (p.brandInput ?? '').trim()

    return {
      uid: makeRowUid(),
      partNumber: partNumberRaw || '???',
      // Оригинальная строка заказчика без количества — для «Артикул заказчика» в КП.
      customerPartNumber: stripTrailingQty(p.rawLine, p.qty),
      brand,
      quantity: p.qty != null && p.qty > 0 ? p.qty : 0,
      unit: p.unit ?? 'шт',
      note: [p.description, p.notes].filter(Boolean).join(' | '),
      priority: 'MEDIUM' as const,
      matrix: matched ? 'Производители' : '',
      category: (p.category ?? '').trim(),
      namingStatus: p.namingStatus,
      namingSource: p.namingSource ?? null,
      dedupCanonical: p.dedupCanonical ?? null,
      dedupReason: p.dedupReason ?? null,
      // Подсказка нетарифки из памяти по партномеру — только показ, в заявку не уходит.
      customsHint: p.customsHint ? { ...p.customsHint, forPartNumber: partNumberRaw } : null,
      // Каталог заменил указанный бренд — оставляем оба, выбор в ячейке «Бренд».
      brandChoice:
        brand && brandInput
          ? { catalog: brand, input: brandInput, forPartNumber: partNumberRaw }
          : null,
      checked: !needsReview,
      lineNumber: findLineNum(p.rawLine),
      needsManualReview: needsReview,
      parseWarnings: warnings,
    }
  })

  // Skipped-строки (парсер не распознал вообще) — тоже в таблицу, как
  // битые позиции: помечены красным (needsManualReview=true), unchecked,
  // в note кладём исходную строку, чтобы менеджер видел оригинал и мог
  // заполнить руками. Без этого нумерация сбивалась: в превью строка №37
  // помечена ✗, а в нижней таблице её нет и под номером 37 идёт следующая.
  const skippedItems: ItemRow[] = []
  for (let i = 0; i < nonEmptyLines.length; i++) {
    if (usedLineIdx[i]) continue
    const line = nonEmptyLines[i] ?? ''
    if (!line) continue
    usedLineIdx[i] = true
    skippedItems.push({
      uid: makeRowUid(),
      partNumber: '???',
      // Нераспознанная строка — оригинал заказчика = вся строка как есть.
      customerPartNumber: line,
      brand: '',
      quantity: 0,
      unit: 'шт',
      note: line,
      priority: 'MEDIUM' as const,
      matrix: '',
      category: '',
      checked: false,
      lineNumber: i + 1,
      needsManualReview: true,
      parseWarnings: ['Парсер не распознал — заполните вручную или удалите'],
    })
  }

  // Rows без lineNumber — это синтетические/расщеплённые позиции от парсера
  // (одну исходную строку с несколькими партномерами он расщепил на 2-3),
  // у них rawLine не совпадает ни с одной строкой исходного текста. В таблицу
  // их НЕ кладём, иначе они идут в конец без номера и ломают «1004 вместо
  // 1000». Их короткие описания собираем в extras — NewRequestPage покажет
  // их в общем warning-блоке, чтобы менеджер не потерял информацию.
  const linkedRowItems: ItemRow[] = []
  const extras: string[] = []
  for (const item of rowItems) {
    if (item.lineNumber == null) {
      const parts = [item.partNumber, item.brand, item.quantity > 0 ? String(item.quantity) : '']
        .map((s) => s.trim())
        .filter(Boolean)
      extras.push(parts.join(' '))
    } else {
      linkedRowItems.push(item)
    }
  }

  // Объединяем rows (с lineNumber) + skipped и сортируем по lineNumber,
  // чтобы порядок совпадал с исходным текстом.
  const items = [...linkedRowItems, ...skippedItems].sort((a, b) => {
    if (a.lineNumber == null && b.lineNumber == null) return 0
    if (a.lineNumber == null) return 1
    if (b.lineNumber == null) return -1
    return a.lineNumber - b.lineNumber
  })

  return { items, extras }
}
